// components/modals/hotspot-profile/CreateHotspotProfile.jsx

import { useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { HotspotProfileService } from "../../../../services/HotspotProfileService";
import { ModalWraper } from "../../../../components/modal/wraper";
import { PppoeProfileService } from "../../../../services/PppoeProfileService";

// ==========================================
// REUSABLE: Input Default
// ==========================================
const InputDefault = ({
    id,
    label,
    type = "text",
    placeholder,
    value,
    onChange,
    hint,
    required = false,
    step,
    min,
    disabled = false,
}) => (
    <div>
        {label && (
            <label
                htmlFor={id}
                className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider"
            >
                {label}
                {required && <span className="text-rose-500 ml-1">*</span>}
            </label>
        )}

        <input
            id={id}
            type={type}
            step={step}
            min={min}
            placeholder={placeholder}
            value={value ?? ""}
            onChange={onChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-xs rounded-xl bg-slate-800/60 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        />

        {hint && (
            <p className="text-xs text-slate-500 mt-1.5">{hint}</p>
        )}
    </div>
);

// ==========================================
// REUSABLE: Select Default
// ==========================================
const SelectDefault = ({
    id,
    label,
    value,
    onChange,
    options,
    placeholder = "Pilih...",
    required = false,
    disabled = false,
}) => (
    <div>
        {label && (
            <label
                htmlFor={id}
                className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider"
            >
                {label}
                {required && <span className="text-rose-500 ml-1">*</span>}
            </label>
        )}

        <select
            id={id}
            value={value ?? ""}
            onChange={onChange}
            disabled={disabled}
            className="w-full px-4 py-3 text-xs rounded-xl bg-slate-800/60 border border-slate-700 text-slate-100 focus:outline-none focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors appearance-none"
            style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
            }}
        >
            <option value="">{placeholder}</option>
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </select>
    </div>
);

// ==========================================
// REUSABLE: Toggle Switch
// ==========================================
const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <label className="flex items-center justify-between cursor-pointer group">
        <div className="flex items-center gap-3">
            <div
                className={`w-10 rounded-full transition-colors relative ${checked ? "bg-cyan-500" : "bg-slate-600"
                    }`}
                style={{ height: "22px" }}
            >
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onChange}
                    className="sr-only"
                />
                <div
                    className={`absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white shadow-sm transition-all ${checked ? "left-[20px]" : "left-[2px]"
                        }`}
                />
            </div>
            <div>
                <span className="text-sm text-slate-200 font-medium block leading-tight">
                    {label}
                </span>
                {description && (
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                        {description}
                    </p>
                )}
            </div>
        </div>
    </label>
);

// ==========================================
// REUSABLE: Toggleable Card (toggle + konten)
// ==========================================
const ToggleableCard = ({
    enabled,
    onToggle,
    label,
    description,
    children,
}) => (
    <div
        className={`rounded-xl border p-4 transition-all ${enabled
            ? "border-cyan-500/30 bg-cyan-500/5"
            : "border-slate-700/40 bg-slate-800/20 opacity-60"
            }`}
    >
        <ToggleSwitch
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            label={label}
            description={description}
        />

        {enabled && (
            <div className="mt-4 space-y-3 pl-[52px]">{children}</div>
        )}
    </div>
);

// ==========================================
// REUSABLE: Section Divider
// ==========================================
const SectionTitle = ({ icon, title }) => (
    <div className="flex items-center gap-2 pt-1 pb-2">
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
            {title}
        </span>
        <div className="flex-1 h-px bg-slate-700/50" />
    </div>
);

// ==========================================
// SMART DEFAULTS PER TYPE
// ==========================================
const TYPE_DEFAULTS = {
    pppoe: {
        validity_unit: "months",
        validity_value: 1,
    },
    voucher: {
        validity_unit: "hours",
        validity_value: 2,
    },
    member: {
        validity_unit: "months",
        validity_value: 1,
    },
};

// ==========================================
// MAIN COMPONENT
// ==========================================
export const PppoeProfileCreate = ({
    refresh,
    openModal,
    setOpenModal,
}) => {
    const [loading, setLoading] = useState(false);

    // ---- Form values ----
    const getInitialFormData = () => ({
        type: "pppoe",
        name: "",

        // Bandwidth (terpisah)
        download: "",
        upload: "",
        burst_download: "",
        burst_upload: "",
        burst_time: "",

        // Optional field values (hanya dikirim jika togglenya ON)
        ip_address: "",
        framed_pool: "",
        session_timeout: "",
        idle_timeout: "",
        total_limit_bytes: "",
        address_list: "",

        // Simple
        simultaneous_use: 1,
        mac_lock: false,

        // Pricing
        price: "",
        cost: "",

        // Validity
        validity_unit: "months",
        validity_value: 1,
    });

    const [formData, setFormData] = useState(getInitialFormData);

    // ---- Toggle states ----
    const getInitialToggles = () => ({
        burst: false,
        staticIp: false,
        framedPool: false,
        sessionTimeout: false,
        idleTimeout: false,
        dataQuota: false,
        addressList: false,
    });

    const [toggles, setToggles] = useState(getInitialToggles);

    // ---- Helpers ----
    const closeModal = () => {
        setOpenModal(false);
        setFormData(getInitialFormData());
        setToggles(getInitialToggles());
        setLoading(false);
    };

    const handleTypeChange = (newType) => {
        const defaults = TYPE_DEFAULTS[newType] || {};
        setFormData((prev) => ({ ...prev, type: newType, ...defaults }));
    };

    const handleField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleNum = (field, raw) => {
        if (raw === "" || raw === null || raw === undefined) {
            setFormData((prev) => ({ ...prev, [field]: "" }));
            return;
        }
        const n = Number(raw);
        if (!isNaN(n) && n >= 0) {
            setFormData((prev) => ({ ...prev, [field]: n }));
        }
    };

    const toggle = (key) => {
        setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    // Margin preview
    const getMargin = () => {
        const price = Number(formData.price) || 0;
        const cost = Number(formData.cost) || 0;
        if (price <= 0) return null;
        const margin = price - cost;
        const pct = ((margin / price) * 100).toFixed(1);
        return { margin, pct };
    };

    // ==================== SUBMIT ====================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert("Nama paket wajib diisi.");
            return;
        }
        if (!formData.download.trim() || !formData.upload.trim()) {
            alert("Download dan Upload wajib diisi.");
            return;
        }

        try {
            setLoading(true);

            // --- Bangun rate_limit string ---
            let rateLimit = `${formData.download.trim()}/${formData.upload.trim()}`;
            if (toggles.burst) {
                const bd = formData.burst_download?.trim();
                const bu = formData.burst_upload?.trim();
                const bt = formData.burst_time?.trim();
                if (bd && bu) {
                    rateLimit += ` ${bd}/${bu}`;
                    if (bt) rateLimit += ` ${bt}`;
                }
            }

            // --- Bangun payload (hanya field yang di-toggle ON) ---
            const body = {
                type: formData.type,
                name: formData.name.trim(),
                rate_limit: rateLimit,
                simultaneous_use: Number(formData.simultaneous_use) || 1,
                mac_lock: formData.mac_lock,
                price: Number(formData.price) || 0,
                cost: Number(formData.cost) || 0,
                validity_unit: formData.validity_unit || null,
                validity_value:
                    formData.validity_value !== ""
                        ? Number(formData.validity_value)
                        : null,
            };

            // Optional: hanya masukkan jika togglenya aktif
            if (toggles.staticIp) {
                body.ip_address = formData.ip_address?.trim() || null;
            }
            if (toggles.framedPool) {
                body.framed_pool = formData.framed_pool?.trim() || null;
            }
            if (toggles.sessionTimeout) {
                body.session_timeout =
                    formData.session_timeout !== ""
                        ? Number(formData.session_timeout)
                        : null;
            }
            if (toggles.idleTimeout) {
                body.idle_timeout =
                    formData.idle_timeout !== ""
                        ? Number(formData.idle_timeout)
                        : null;
            }
            if (toggles.dataQuota) {
                body.total_limit_bytes =
                    formData.total_limit_bytes !== ""
                        ? Number(formData.total_limit_bytes)
                        : null;
            }
            if (toggles.addressList) {
                body.address_list = formData.address_list?.trim() || null;
            }

            console.log("Payload create profile:", body);

            await PppoeProfileService.create({ data: body });

            if (refresh) refresh();
            closeModal();
        } catch (err) {
            console.error("Gagal membuat profil:", err);
            const msg =
                err?.response?.data?.message || "Gagal membuat profil.";
            alert(msg);
        } finally {
            setLoading(false);
        }
    };

    // ==================== RENDER ====================
    const margin = getMargin();

    return (
        <ModalWraper
            isOpen={openModal}
            setIsOpen={setOpenModal}
            title="Buat Profil Baru"
            maxWidth={"max-w-[1000px]"}
        >
            <form onSubmit={handleSubmit} className="space-y-5" >
                {/* ============================== */}
                {/* TIPE PROFIL                    */}
                {/* ============================== */}
                {/* <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                        Tipe Profil
                    </label>

                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { value: "pppoe", label: "PPPoE", icon: "🔌" },
                            { value: "voucher", label: "Voucher", icon: "🎫" },
                            { value: "member", label: "Member", icon: "👤" },
                        ].map((item) => (
                            <label
                                key={item.value}
                                className={`cursor-pointer rounded-xl border px-3 py-3 transition-all ${
                                    formData.type === item.value
                                        ? "border-cyan-500 bg-cyan-500/10"
                                        : "border-slate-700 hover:bg-slate-800/50"
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="profile_type"
                                    value={item.value}
                                    checked={formData.type === item.value}
                                    onChange={(e) =>
                                        handleTypeChange(e.target.value)
                                    }
                                    className="sr-only"
                                />
                                <div className="flex flex-col items-center gap-1.5">
                                    <div
                                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                            formData.type === item.value
                                                ? "border-cyan-500"
                                                : "border-slate-500"
                                        }`}
                                    >
                                        {formData.type === item.value && (
                                            <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
                                        )}
                                    </div>
                                    <span className="text-xl">{item.icon}</span>
                                    <span className="font-medium text-xs text-slate-200">
                                        {item.label}
                                    </span>
                                </div>
                            </label>
                        ))}
                    </div>
                </div> */}

                <div className="grid grid-1 md:grid-cols-2 gap-4 ">
                    <div className="space-y-4">
                        {/* ============================== */}
                        {/* INFORMASI DASAR                */}
                        {/* ============================== */}
                        <SectionTitle icon="📋" title="Informasi Dasar" />

                        <InputDefault
                            id="name"
                            label="Nama Paket"
                            placeholder="Paket Home 10M, Voucher 2 Jam..."
                            value={formData.name}
                            onChange={(e) => handleField("name", e.target.value)}
                            required
                        />

                        {/* --- BANDWIDTH --- */}
                        <div className="rounded-xl border border-slate-700/60 bg-slate-800/30 p-4 space-y-3">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm">⚡</span>
                                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                    Bandwidth
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <InputDefault
                                    id="download"
                                    label="Download"
                                    placeholder="10M"
                                    value={formData.download}
                                    onChange={(e) =>
                                        handleField("download", e.target.value)
                                    }
                                    hint="Contoh: 10M, 5M, 1G"
                                    required
                                />
                                <InputDefault
                                    id="upload"
                                    label="Upload"
                                    placeholder="5M"
                                    value={formData.upload}
                                    onChange={(e) =>
                                        handleField("upload", e.target.value)
                                    }
                                    hint="Contoh: 5M, 2M, 500K"
                                    required
                                />
                            </div>

                            {/* Burst — toggle */}
                            <ToggleableCard
                                enabled={toggles.burst}
                                onToggle={() => toggle("burst")}
                                label="Aktifkan Burst"
                                description="Kecepatan naik sesaat sebelum turun ke limit normal"
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <InputDefault
                                        id="burst_download"
                                        label="Burst Download"
                                        placeholder="12M"
                                        value={formData.burst_download}
                                        onChange={(e) =>
                                            handleField("burst_download", e.target.value)
                                        }
                                    />
                                    <InputDefault
                                        id="burst_upload"
                                        label="Burst Upload"
                                        placeholder="8M"
                                        value={formData.burst_upload}
                                        onChange={(e) =>
                                            handleField("burst_upload", e.target.value)
                                        }
                                    />
                                    {/* <InputDefault
                id="burst_time"
                label="Burst Time"
                type="number"
                min="0"
                placeholder="10"
                value={formData.burst_time}
                onChange={(e) =>
                    handleNum("burst_time", e.target.value)
                }
                hint="Detik"
            /> */}
                                </div>
                            </ToggleableCard>
                        </div>
                    </div>

                    {/* ============================== */}
                    {/* PENGATURAN LANJUTAN            */}
                    {/* ============================== */}
                    {/* <SectionTitle icon="⚙️" title="Pengaturan Lanjutan" />

                <div className="space-y-3">
                    // Static IP
                    <ToggleableCard
                        enabled={toggles.staticIp}
                        onToggle={() => toggle("staticIp")}
                        label="Static IP"
                        description="Berikan IP tetap ke pengguna"
                    >
                        <InputDefault
                            id="ip_address"
                            placeholder="192.168.1.100"
                            value={formData.ip_address}
                            onChange={(e) =>
                                handleField("ip_address", e.target.value)
                            }
                        />
                    </ToggleableCard>

                   // Framed Pool
                    <ToggleableCard
                        enabled={toggles.framedPool}
                        onToggle={() => toggle("framedPool")}
                        label="Framed Pool"
                        description="Assign IP dari pool tertentu di MikroTik"
                    >
                        <InputDefault
                            id="framed_pool"
                            placeholder="pool-pppoe, dhcp_pool1..."
                            value={formData.framed_pool}
                            onChange={(e) =>
                                handleField("framed_pool", e.target.value)
                            }
                        />
                    </ToggleableCard>

                    // Session Timeout
                    <ToggleableCard
                        enabled={toggles.sessionTimeout}
                        onToggle={() => toggle("sessionTimeout")}
                        label="Session Timeout"
                        description="Maksimal durasi koneksi (dalam detik)"
                    >
                        <InputDefault
                            id="session_timeout"
                            type="number"
                            min="0"
                            placeholder="7200"
                            value={formData.session_timeout}
                            onChange={(e) =>
                                handleNum("session_timeout", e.target.value)
                            }
                            hint="2 jam = 7200 detik"
                        />
                    </ToggleableCard>

                    // Idle Timeout
                    <ToggleableCard
                        enabled={toggles.idleTimeout}
                        onToggle={() => toggle("idleTimeout")}
                        label="Idle Timeout"
                        description="Putus koneksi jika tidak ada aktivitas data"
                    >
                        <InputDefault
                            id="idle_timeout"
                            type="number"
                            min="0"
                            placeholder="600"
                            value={formData.idle_timeout}
                            onChange={(e) =>
                                handleNum("idle_timeout", e.target.value)
                            }
                            hint="10 menit = 600 detik"
                        />
                    </ToggleableCard>

                    // Kuota Data
                    <ToggleableCard
                        enabled={toggles.dataQuota}
                        onToggle={() => toggle("dataQuota")}
                        label="Kuota Data"
                        description="Batasi total upload + download dalam bytes"
                    >
                        <InputDefault
                            id="total_limit_bytes"
                            type="number"
                            min="0"
                            placeholder="1073741824"
                            value={formData.total_limit_bytes}
                            onChange={(e) =>
                                handleNum("total_limit_bytes", e.target.value)
                            }
                            hint="1 GB = 1073741824"
                        />
                    </ToggleableCard>

                    // Address List
                    <ToggleableCard
                        enabled={toggles.addressList}
                        onToggle={() => toggle("addressList")}
                        label="Address List"
                        description="Otomatis masukkan IP ke Address List MikroTik"
                    >
                        <InputDefault
                            id="address_list"
                            placeholder="list-pelanggan-aktif"
                            value={formData.address_list}
                            onChange={(e) =>
                                handleField("address_list", e.target.value)
                            }
                        />
                    </ToggleableCard>

                    // MAC Lock — simple checkbox, tanpa card
                    <div className="rounded-xl border border-slate-700/40 bg-slate-800/20 p-4">
                        <ToggleSwitch
                            checked={formData.mac_lock}
                            onChange={(e) =>
                                handleField("mac_lock", e.target.checked)
                            }
                            label="MAC Lock"
                            description="Kunci ke MAC address saat login pertama"
                        />
                    </div>

                    //Simultaneous Use — selalu tampil
                    <div className="rounded-xl border border-slate-700/40 bg-slate-800/20 p-4">
                        <InputDefault
                            id="simultaneous_use"
                            label="Simultaneous Use"
                            type="number"
                            min="1"
                            placeholder="1"
                            value={formData.simultaneous_use}
                            onChange={(e) =>
                                handleNum("simultaneous_use", e.target.value)
                            }
                            hint="Jumlah perangkat yang bisa login bersamaan"
                        />
                    </div>
                </div> */}

                    <div className="space-y-4">
                        {/* ============================== */}
                        {/* HARGA & BILLING                */}
                        {/* ============================== */}
                        <SectionTitle icon="💰" title="Harga & Billing" />

                        <div className="grid grid-cols-2 gap-4">
                            <InputDefault
                                id="price"
                                label="Harga Jual (Rp)"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="150000"
                                value={formData.price}
                                onChange={(e) => handleNum("price", e.target.value)}
                                required
                            />
                            <InputDefault
                                id="cost"
                                label="HPP / Modal (Rp)"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="100000"
                                value={formData.cost}
                                onChange={(e) => handleNum("cost", e.target.value)}
                            />
                        </div>

                        {margin && (
                            <div className="flex items-center gap-4 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/50">
                                <div className="flex-1">
                                    <p className="text-xs text-slate-500">Margin</p>
                                    <p className="text-sm font-bold text-emerald-400">
                                        Rp {margin.margin.toLocaleString("id-ID")}
                                    </p>
                                </div>
                                <div className="w-px h-8 bg-slate-700" />
                                <div className="flex-1">
                                    <p className="text-xs text-slate-500">Persentase</p>
                                    <p className="text-sm font-bold text-emerald-400">
                                        {margin.pct}%
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ============================== */}
                {/* BUTTONS                        */}
                {/* ============================== */}
                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 transition-colors hover:bg-slate-800"
                    >
                        Batal
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                        {loading ? (
                            <>
                                <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                Menyimpan...
                            </>
                        ) : (
                            <>
                                <FaPlus />
                                Buat Profil
                            </>
                        )}
                    </button>
                </div>
            </form>
        </ModalWraper>
    );
};